import React, { useEffect, useState } from 'react'
import { AxiosError } from 'axios'
import { useQuery } from 'react-query'
import {
  Calendar,
  BookOpen,
  RefreshCw,
  AlertCircle,
  X,
  Clock,
  Sparkles,
  Link as LinkIcon,
  Info,
  Layers
} from 'lucide-react'
import { api } from '../../lib/api'

interface LearningPlan {
  id: number
  title: string
  subject: string
  startDate: string
  endDate: string
  diff: string
}

interface LearningPlansResponse {
  student: {
    id: string
    vorname: string
    name: string
    klasse: string
  }
  plans: LearningPlan[]
  lookupDebug?: Record<string, unknown>
}

interface Competency {
  id: number
  name: string
  description: string | null
  weight: number
  required: boolean
}

interface LearningPlanTask {
  id: number
  title: string | null
  description: string | null
  minutes: number | null
  position: number | null
  sortOrder: number | null
  isBonus: boolean
  isDifficult: boolean
  hasEvidence?: boolean
  competencies?: Competency[]
  workForms: {
    individual: boolean
    partner: boolean
    group: boolean
    pcTablet: boolean
  }
  socialForm: string | null
  materials: string | null
  info: string | null
  link: string | null
}

interface LearningPlanDetail {
  plan: {
    id: number
    title: string
    subject: string | null
    diff: string | null
    startDate: string | null
    endDate: string | null
    learningGoals: string | null
    status: number | null
    competencies?: Competency[]
    baustein: {
      id: number | null
      name: string | null
      grade: string | null
    }
    className: string | null
    timeframe: {
      status: 'active' | 'upcoming' | 'completed' | 'unknown'
      daysUntilStart: number | null
      daysRemaining: number | null
    }
  }
  tasks: LearningPlanTask[]
  metrics: {
    totalTasks: number
    regularTasks: number
    bonusTasks: number
    totalEstimatedMinutes: number
    hasDifficultTasks: boolean
  }
}

const fetchLearningPlans = async (): Promise<LearningPlansResponse> => {
  const response = await api.get('/student/learning-plans/active')
  return response.data.data
}

const fetchLearningPlanDetail = async (planId: number): Promise<LearningPlanDetail> => {
  const response = await api.get(`/student/learning-plans/${planId}`)
  return response.data.data
}

const groupBySubject = (plans: LearningPlan[]) => {
  return plans.reduce<Record<string, LearningPlan[]>>((acc, plan) => {
    const key = plan.subject || 'Sonstiges'
    if (!acc[key]) {
      acc[key] = []
    }
    acc[key].push(plan)
    return acc
  }, {})
}

const formatDate = (value?: string | null) => {
  if (!value) return 'unbekannt'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return 'unbekannt'
  }
  return date.toLocaleDateString('de-DE')
}

const formatMinutes = (minutes?: number | null) => {
  if (minutes === null || minutes === undefined || Number.isNaN(minutes) || minutes <= 0) {
    return 'Keine Angabe'
  }

  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60)
    const remaining = minutes % 60
    return `${hours} h${remaining ? ` ${remaining} min` : ''}`
  }

  return `${minutes} min`
}

const getWorkFormLabels = (forms: LearningPlanTask['workForms']) => {
  const labels: string[] = []
  if (forms.individual) labels.push('Einzelarbeit')
  if (forms.partner) labels.push('Partnerarbeit')
  if (forms.group) labels.push('Gruppenarbeit')
  if (forms.pcTablet) labels.push('PC/Tablet')
  return labels
}

const hasMeaningfulValue = (value?: string | null) => {
  if (!value) {
    return false
  }
  const trimmed = value.trim()
  return trimmed !== '' && trimmed !== '-' && trimmed.toLowerCase() !== 'null'
}

const TIMEFRAME_STATUS_LABELS: Record<string, string> = {
  active: 'Aktiv',
  upcoming: 'Noch nicht gestartet',
  completed: 'Abgeschlossen',
  unknown: 'Zeitraum unbekannt'
}

const TIMEFRAME_STATUS_STYLES: Record<string, string> = {
  active: 'bg-green-100 text-green-700',
  upcoming: 'bg-amber-100 text-amber-700',
  completed: 'bg-gray-200 text-gray-700',
  unknown: 'bg-gray-100 text-gray-600'
}

const StudentLearningPlans: React.FC = () => {
  const isDebugMode = import.meta.env.DEV
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null)
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isFetching
  } = useQuery<LearningPlansResponse, Error>('student-learning-plans', fetchLearningPlans, {
    staleTime: 60 * 1000
  })

  const {
    data: detailData,
    isLoading: isDetailLoading,
    isError: isDetailError,
    error: detailError
  } = useQuery<LearningPlanDetail, Error>(
    ['student-learning-plan-detail', selectedPlanId],
    () => fetchLearningPlanDetail(selectedPlanId!),
    {
      enabled: selectedPlanId !== null,
      staleTime: 5 * 60 * 1000,
      cacheTime: 5 * 60 * 1000
    }
  )

  useEffect(() => {
    if (selectedPlanId === null) {
      return
    }

    const previousOverflow = document.body.style.overflow
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedPlanId(null)
      }
    }

    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', handleKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [selectedPlanId])

  const renderLoading = () => (
    <div className="flex items-center justify-center py-16">
      <div className="flex flex-col items-center space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
        <p className="text-gray-500 text-sm">Deine Lernpläne werden geladen...</p>
      </div>
    </div>
  )

  const renderError = () => {
    const axiosError = error as AxiosError<{ message?: string; debug?: Record<string, unknown> }>
    const errorMessage = axiosError?.response?.data?.message || error?.message || 'Die Liste der Lernpläne konnte nicht geladen werden. Bitte versuche es erneut.'
    const errorDebug = axiosError?.response?.data?.debug

    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 space-y-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
          <div>
            <h2 className="text-lg font-semibold text-red-700">Fehler beim Laden</h2>
            <p className="text-red-600 text-sm mt-1">
              {errorMessage}
            </p>
            <button
              onClick={() => refetch()}
              className="mt-4 inline-flex items-center px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            >
              Erneut versuchen
            </button>
          </div>
        </div>

        {isDebugMode && errorDebug && (
          <div className="bg-red-900/80 text-red-100 rounded-lg border border-red-800 p-4">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-red-200">Debug-Daten</h3>
            <pre className="mt-2 text-xs whitespace-pre-wrap break-all">
              {JSON.stringify(errorDebug, null, 2)}
            </pre>
          </div>
        )}
      </div>
    )
  }

  if (isLoading) {
    return renderLoading()
  }

  if (isError || !data) {
    return renderError()
  }

  const { student, plans } = data
  const plansBySubject = groupBySubject(plans)
  const subjectKeys = Object.keys(plansBySubject).sort()
  const closeDetails = () => setSelectedPlanId(null)

  const renderDetailModal = () => {
    if (selectedPlanId === null) {
      return null
    }

    let body: React.ReactNode

    if (isDetailLoading) {
      body = (
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center space-y-4">
            <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-primary-600" />
            <p className="text-sm text-gray-500">Lernplan wird geladen...</p>
          </div>
        </div>
      )
    } else if (isDetailError) {
      const axiosError = detailError as AxiosError<{ message?: string }>
      const message =
        axiosError?.response?.data?.message ||
        detailError?.message ||
        'Die Lernplan-Details konnten nicht geladen werden. Bitte versuche es erneut.'

      body = (
        <div className="rounded-lg border border-red-200 bg-red-50 p-6">
          <h3 className="text-lg font-semibold text-red-700">Fehler beim Laden</h3>
          <p className="mt-2 text-sm text-red-600">{message}</p>
        </div>
      )
    } else if (!detailData) {
      body = (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-sm text-gray-600">
          Die Lernplan-Details sind derzeit nicht verfügbar.
        </div>
      )
    } else {
      const { plan, tasks, metrics } = detailData
      const statusLabel = TIMEFRAME_STATUS_LABELS[plan.timeframe.status] ?? TIMEFRAME_STATUS_LABELS.unknown
      const statusBadgeClass =
        TIMEFRAME_STATUS_STYLES[plan.timeframe.status] ?? TIMEFRAME_STATUS_STYLES.unknown

      const workFormChips = tasks.map((task) => getWorkFormLabels(task.workForms))

      body = (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Fach</p>
              <p className="mt-1 text-sm font-semibold text-gray-900">{plan.subject || 'Unbekannt'}</p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Differenzierung</p>
              <p className="mt-1 text-sm font-semibold text-gray-900">{plan.diff || 'Standard'}</p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Zeitraum</p>
              <p className="mt-1 text-sm text-gray-700">
                {formatDate(plan.startDate)} – {formatDate(plan.endDate)}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Status</p>
              <span
                className={`mt-1 inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${statusBadgeClass}`}
              >
                {statusLabel}
              </span>
              {plan.timeframe.status === 'active' && plan.timeframe.daysRemaining !== null && (
                <p className="mt-2 text-xs text-gray-500">
                  Noch {plan.timeframe.daysRemaining} Tag(e) verbleibend.
                </p>
              )}
              {plan.timeframe.status === 'upcoming' && plan.timeframe.daysUntilStart !== null && (
                <p className="mt-2 text-xs text-gray-500">
                  Start in {plan.timeframe.daysUntilStart} Tag(en).
                </p>
              )}
            </div>
          </div>

          {(plan.baustein.name || plan.className) && (
            <div className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3 text-sm text-gray-700">
              {plan.baustein.name && (
                <p>
                  <span className="font-medium text-gray-800">Baustein:</span> {plan.baustein.name}
                  {plan.baustein.grade ? ` · Jahrgang ${plan.baustein.grade}` : ''}
                </p>
              )}
              {plan.className && (
                <p>
                  <span className="font-medium text-gray-800">Klasse:</span> {plan.className}
                </p>
              )}
            </div>
          )}

          {plan.learningGoals && hasMeaningfulValue(plan.learningGoals) && (
            <div className="rounded-lg border border-primary-100 bg-primary-50 px-4 py-3">
              <h3 className="text-sm font-semibold text-primary-900">Lernziele</h3>
              <p className="mt-2 whitespace-pre-wrap text-sm text-primary-800">
                {plan.learningGoals.trim()}
              </p>
            </div>
          )}

          {plan.competencies && plan.competencies.length > 0 && (
            <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3">
              <h3 className="text-sm font-semibold text-blue-900 mb-2">Kompetenzen des Lernplans</h3>
              <div className="flex flex-wrap gap-2">
                {plan.competencies.map((competency) => (
                  <span
                    key={competency.id}
                    className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-800"
                    title={competency.description || competency.name}
                  >
                    {competency.name}
                    {competency.required && (
                      <span className="ml-1 text-blue-600">*</span>
                    )}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Aufgaben gesamt</p>
              <p className="mt-1 text-xl font-semibold text-gray-900">{metrics.totalTasks}</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Bonusaufgaben</p>
              <p className="mt-1 text-xl font-semibold text-gray-900">{metrics.bonusTasks}</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Geplanter Zeitaufwand</p>
              <p className="mt-1 flex items-center gap-2 text-xl font-semibold text-gray-900">
                <Clock className="h-5 w-5 text-primary-600" />
                {formatMinutes(metrics.totalEstimatedMinutes)}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-base font-semibold text-gray-900">Aufgabenübersicht</h3>
            {tasks.length === 0 ? (
              <p className="text-sm text-gray-600">
                Für diesen Lernplan sind noch keine Aufgaben hinterlegt.
              </p>
            ) : (
              tasks.map((task, index) => {
                const labels = workFormChips[index]
                const hasEvidence = task.hasEvidence ?? false
                return (
                  <div 
                    key={task.id} 
                    className={`rounded-lg border p-4 shadow-sm ${
                      hasEvidence 
                        ? 'border-green-300 bg-green-50' 
                        : 'border-gray-200 bg-white'
                    }`}
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Aufgabe {index + 1}
                        </p>
                        <h4 className="mt-1 text-lg font-semibold text-gray-900">
                          {task.title || `Aufgabe ${index + 1}`}
                        </h4>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        {task.minutes !== null && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700">
                            <Clock className="h-3.5 w-3.5" />
                            {formatMinutes(task.minutes)}
                          </span>
                        )}
                        {task.isBonus && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-700">
                            <Sparkles className="h-3.5 w-3.5" />
                            Bonus
                          </span>
                        )}
                        {task.isDifficult && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-3 py-1 text-xs font-medium text-rose-700">
                            <Info className="h-3.5 w-3.5" />
                            Anspruchsvoll
                          </span>
                        )}
                      </div>
                    </div>

                    {task.description && (
                      <p className="mt-3 whitespace-pre-wrap text-sm text-gray-700">
                        {task.description}
                      </p>
                    )}

                    {labels.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2 text-xs font-medium text-gray-600">
                        {labels.map((label) => (
                          <span
                            key={label}
                            className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1"
                          >
                            {label}
                          </span>
                        ))}
                      </div>
                    )}

                    {hasMeaningfulValue(task.socialForm) && (
                      <p className="mt-3 text-sm text-gray-600">
                        <span className="font-medium text-gray-800">Sozialform:</span> {task.socialForm}
                      </p>
                    )}

                    {hasMeaningfulValue(task.materials) && (
                      <p className="mt-3 flex items-center gap-2 text-sm text-gray-600">
                        <Layers className="h-4 w-4 text-primary-500" />
                        <span>
                          <span className="font-medium text-gray-800">Materialien:</span>{' '}
                          {task.materials}
                        </span>
                      </p>
                    )}

                    {hasMeaningfulValue(task.info) && (
                      <p className="mt-3 flex items-center gap-2 text-sm text-gray-600">
                        <Info className="h-4 w-4 text-primary-500" />
                        <span>{task.info}</span>
                      </p>
                    )}

                    {hasMeaningfulValue(task.link) && (
                      <p className="mt-3 flex items-center gap-2 text-sm text-primary-600">
                        <LinkIcon className="h-4 w-4" />
                        <a
                          href={task.link!}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="break-all underline decoration-primary-400 hover:text-primary-700"
                        >
                          {task.link}
                        </a>
                      </p>
                    )}

                    {task.competencies && task.competencies.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs font-medium text-gray-700 mb-2">Kompetenzen:</p>
                        <div className="flex flex-wrap gap-2">
                          {task.competencies.map((competency) => (
                            <span
                              key={competency.id}
                              className="inline-flex items-center rounded-full bg-indigo-100 px-2.5 py-1 text-xs font-medium text-indigo-800"
                              title={competency.description || competency.name}
                            >
                              {competency.name}
                              {competency.required && (
                                <span className="ml-1 text-indigo-600">*</span>
                              )}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })
            )}
          </div>
        </div>
      )
    }

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8">
        <div
          className="absolute inset-0 bg-gray-900/60"
          aria-hidden="true"
          onClick={closeDetails}
        />
        <div className="relative z-10 flex h-full w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl md:max-h-[90vh]">
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 flex-shrink-0">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Lernplan</p>
              <h2 className="text-lg font-semibold text-gray-900">
                {detailData?.plan.title || 'Lernplan'}
              </h2>
            </div>
            <button
              type="button"
              onClick={closeDetails}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 hover:border-primary-200 hover:text-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              aria-label="Detailansicht schließen"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-6 py-6 min-h-0">
            {body}
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Meine Lernpläne</h1>
            <p className="mt-1 text-gray-600">
              Hier findest du alle Lernpläne, die aktuell für dich aktiv sind. Klasse {student.klasse}.
            </p>
          </div>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-60"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            Aktualisieren
          </button>
        </div>

        {plans.length === 0 ? (
          <div className="flex flex-col items-center rounded-xl border border-gray-100 bg-white p-8 text-center shadow-sm">
            <BookOpen className="h-12 w-12 text-gray-300" />
            <h2 className="mt-4 text-lg font-semibold text-gray-800">Keine aktiven Lernpläne</h2>
            <p className="mt-2 text-sm text-gray-600">
              Aktuell sind keine Lernpläne für dich aktiv. Schau später noch einmal vorbei oder wende dich an deine
              Lehrkraft.
            </p>
          </div>
        ) : (
          subjectKeys.map((subject) => (
            <div
              key={subject}
              className="space-y-4 rounded-xl border border-gray-100 bg-white p-6 shadow-sm transition hover:shadow-md"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100">
                  <BookOpen className="h-5 w-5 text-primary-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{subject}</h2>
                  <p className="text-sm text-gray-600">
                    {plansBySubject[subject].length} aktive Lernplan(e)
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {plansBySubject[subject].map((plan) => (
                  <button
                    key={plan.id}
                    type="button"
                    onClick={() => setSelectedPlanId(plan.id)}
                    className="flex flex-col items-start rounded-lg border border-gray-200 bg-gray-50 p-4 text-left shadow-sm transition hover:border-primary-200 hover:bg-white hover:shadow"
                  >
                    <h3 className="text-base font-semibold text-gray-900">{plan.title}</h3>
                    <p className="mt-1 text-sm text-gray-600">
                      Differenzierung: <span className="font-medium text-gray-800">{plan.diff || 'Standard'}</span>
                    </p>
                    <div className="mt-3 flex items-center text-sm text-gray-600">
                      <Calendar className="mr-2 h-4 w-4 text-primary-500" />
                      <div className="space-y-1">
                        <p>
                          <span className="font-medium text-gray-700">Start:</span> {formatDate(plan.startDate)}
                        </p>
                        <p>
                          <span className="font-medium text-gray-700">Ende:</span> {formatDate(plan.endDate)}
                        </p>
                      </div>
                    </div>
                    <span className="mt-4 inline-flex items-center rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700">
                      Details anzeigen
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {renderDetailModal()}
    </>
  )
}

export default StudentLearningPlans

